import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../DatePicker/DatePicker';
import { tasksService, type TaskApi } from '../../../services/tasksService';
import './TaskDeadlineRequests.css';

const statusLabels = { pending: 'Ожидает решения', approved: 'Согласован', rejected: 'Отклонён' };
const moscowInputValue = (timestamp: number) => new Date(timestamp + 3 * 60 * 60 * 1000).toISOString().slice(0, 16);
const errorMessage = (error: any) => error?.response?.data?.message ?? error?.message ?? 'Не удалось сохранить запрос';

const TaskDeadlineRequests: React.FC<{
  task: TaskApi;
  viewerId: number | null;
  onChanged: (task: TaskApi) => Promise<void>;
}> = ({ task, viewerId, onChanged }) => {
  const [requestedDate, setRequestedDate] = useState('');
  const [reason, setReason] = useState('');
  const [reviewComments, setReviewComments] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState(() => new Date());
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const dateFieldRef = useRef<HTMLButtonElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const requests = task.deadline_requests ?? [];
  const isPerformer = task.performers.some((performer) => performer.id === viewerId);
  const active = !['completed', 'cancelled'].includes(task.status);
  const hasOwnPending = requests.some((request) => request.status === 'pending' && request.requester?.id === viewerId);
  const minimumTimestamp = Math.max(task.deadline ? new Date(task.deadline).getTime() : 0, Date.now()) + 60000;

  const openPicker = () => {
    if (busy) return;
    // DatePicker works with local calendar components; these components represent Moscow wall time.
    setPickerDate(new Date(requestedDate || moscowInputValue(minimumTimestamp)));
    setPickerOpen(true);
  };
  const confirmDate = (date: Date) => {
    const pad = (number: number) => String(number).padStart(2, '0');
    setRequestedDate(`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`);
    setPickerOpen(false);
    dateFieldRef.current?.focus();
  };
  const requestedDateLabel = requestedDate
    ? `${requestedDate.slice(8, 10)}.${requestedDate.slice(5, 7)}.${requestedDate.slice(0, 4)} ${requestedDate.slice(11, 16)}`
    : 'Выберите дату и время';

  useLayoutEffect(() => {
    if (!pickerOpen) return;
    const reposition = () => {
      const field = dateFieldRef.current?.getBoundingClientRect();
      const picker = pickerRef.current?.getBoundingClientRect();
      if (!field || !picker) return;
      const below = field.bottom + 8;
      const above = field.top - picker.height - 8;
      setPickerPosition({
        left: Math.max(12, Math.min(field.left, window.innerWidth - picker.width - 12)),
        top: below + picker.height <= window.innerHeight - 12 ? below
          : above >= 12 ? above : Math.max(12, window.innerHeight - picker.height - 12),
      });
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => { window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true); };
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!pickerRef.current?.contains(target) && !dateFieldRef.current?.contains(target)) setPickerOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      event.preventDefault();
      setPickerOpen(false);
      dateFieldRef.current?.focus();
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onEscape, true);
    return () => { document.removeEventListener('mousedown', onOutside); document.removeEventListener('keydown', onEscape, true); };
  }, [pickerOpen]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const date = new Date(`${requestedDate}+03:00`);
    if (!reason.trim() || !Number.isFinite(date.getTime())) {
      setError('Укажите новый срок и причину переноса');
      return;
    }
    if (date.getTime() <= Date.now() || (task.deadline && date.getTime() <= new Date(task.deadline).getTime())) {
      setError('Новый срок должен быть позже текущего срока и текущего времени');
      return;
    }
    setBusy(true); setError(null); setSuccess(null);
    try {
      const updated = await tasksService.requestDeadlineExtension(task.id, { requested_deadline: date.toISOString(), reason: reason.trim() });
      await onChanged(updated);
      setReason(''); setRequestedDate(''); setSuccess('Запрос отправлен постановщику. Срок изменится после согласования.');
    } catch (failure) { setError(errorMessage(failure)); }
    finally { setBusy(false); }
  };

  const review = async (requestId: number, decision: 'approve' | 'reject') => {
    if (busy) return;
    setBusy(true); setError(null); setSuccess(null);
    try {
      const updated = await tasksService.reviewDeadlineExtension(task.id, requestId, { decision, comment: reviewComments[requestId]?.trim() || undefined });
      await onChanged(updated);
      setSuccess(decision === 'approve' ? 'Перенос согласован, срок задачи изменён.' : 'Запрос отклонён, срок задачи сохранён.');
    } catch (failure) { setError(errorMessage(failure)); }
    finally { setBusy(false); }
  };

  return (
    <div className="task-deadline-requests">
      <h3>Перенос срока задачи</h3>
      <p>Текущий срок: <strong>{task.deadline_formatted || 'Не указан'}</strong></p>
      {error && <p className="task-deadline-error" role="alert">{error}</p>}
      {success && <p className="task-deadline-success" role="status">{success}</p>}
      {isPerformer && active && task.deadline && !hasOwnPending && (
        <form onSubmit={submit} className="task-deadline-form">
          <label htmlFor="requested-task-deadline">Новый срок (МСК)</label>
          <button id="requested-task-deadline" ref={dateFieldRef} type="button" className="task-deadline-date-field" onClick={openPicker} disabled={busy} aria-haspopup="dialog" aria-expanded={pickerOpen} aria-controls={pickerOpen ? 'task-deadline-datepicker' : undefined}>
            <span className={!requestedDate ? 'task-deadline-date-placeholder' : undefined}>{requestedDateLabel}</span>
            <img src="/icons/case/Calendar.svg" alt="" width="20" height="20" />
          </button>
          <label htmlFor="task-deadline-reason">Причина переноса</label>
          <textarea id="task-deadline-reason" maxLength={2000} value={reason} onChange={(event) => setReason(event.target.value)} required disabled={busy} placeholder="Почему нужно увеличить срок" />
          <button type="submit" disabled={busy}>Запросить увеличение срока</button>
        </form>
      )}
      {pickerOpen && createPortal(
        <div id="task-deadline-datepicker" ref={pickerRef} className="task-deadline-datepicker" style={pickerPosition}>
          <DatePicker value={pickerDate} onConfirm={confirmDate} onClose={() => setPickerOpen(false)} />
        </div>, document.body
      )}
      {hasOwnPending && active && <p>Ваш запрос ожидает решения постановщика.</p>}
      {!active && <p>Задача закрыта. Запросить перенос срока нельзя.</p>}
      {isPerformer && active && !task.deadline && <p>Сначала постановщик должен указать срок задачи.</p>}
      {requests.length === 0 && <p>Запросов на перенос срока пока нет.</p>}
      {requests.map((request) => (
        <section className="task-deadline-request" key={request.id}>
          <div className="task-deadline-request-header"><strong>{request.requester?.name || 'Исполнитель'}</strong><span>{statusLabels[request.status]}</span></div>
          <small>{request.created_at_formatted}</small>
          <p>{request.original_deadline_formatted} → <strong>{request.requested_deadline_formatted}</strong></p>
          <p className="task-deadline-reason">{request.reason}</p>
          {request.review_comment && <p className="task-deadline-reason">Комментарий постановщика: {request.review_comment}</p>}
          {task.viewer_is_assigner && request.status === 'pending' && (
            <div className="task-deadline-form">
              <label htmlFor={`deadline-review-${request.id}`}>Комментарий к решению</label>
              <textarea id={`deadline-review-${request.id}`} maxLength={2000} value={reviewComments[request.id] || ''} onChange={(event) => setReviewComments((current) => ({ ...current, [request.id]: event.target.value }))} disabled={busy} />
              <div className="task-deadline-actions">
                <button type="button" disabled={busy || !active} onClick={() => review(request.id, 'approve')}>Согласовать</button>
                <button type="button" disabled={busy} onClick={() => review(request.id, 'reject')}>Отклонить</button>
              </div>
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

export default TaskDeadlineRequests;
